<script setup>
import SidebarNavigationItem from './sidebarNavigationItem.vue';
import SidebarNavigationHeading from './sidebarNavigationHeading.vue';
import SidebarDock from './sidebarDock.vue';
import SidebarFixedActions from './sidebarFixedActions.vue';
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
} from 'vue';

const ISLAND_OPEN_DELAY = 100;
const ISLAND_CLOSE_DELAY = 200;
const ISLAND_VIEWPORT_MARGIN = 8;

const props = defineProps({
    items: {
        type: Array,
        required: false,
        default: () => [],
    },
    hiddenItems: {
        type: Object,
        required: false,
        default: () => ({
            headers: [],
            items: [],
        }),
    },
    showLabels: {
        type: Boolean,
        default: true,
    },
    notificationCount: {
        type: Number,
        default: 0,
    },
    userProfileImageUrl: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['open-notifications']);

const notifications = reactive({
    dailyAssistant: 0,
    setupAssistant: 0,
});

const expandedSections = ref([]);
const dashboardNavigationItem = {
    name: 'navigation:backOffice.sidebar.dashboard.title',
    href: route('boIndex'),
    icon: 'fa-gauge-high',
    hasSubnav: false,
    itemName: 'dashboard',
    routeName: 'boIndex',
    activeRoutes: ['boIndex'],
    notificationsCount: false,
};

const loadNotificationCounts = async () => {
    const res = await axios.get(route('notifications.sidebar'));
    res.data.sections.forEach((section) => {
        notifications[section.name] = section.count;
    });
};

const toggleSection = (item) => {
    if (expandedSections.value.includes(item.name)) {
        expandedSections.value = expandedSections.value.filter(
            (section) => section !== item.name
        );
    } else {
        expandedSections.value.push(item.name);
    }

    localStorage.setItem(
        'expandedSections',
        JSON.stringify(expandedSections.value)
    );
};

const collapseExpandedSections = () => {
    expandedSections.value = [];
    localStorage.setItem('expandedSections', JSON.stringify([]));
};

const filteredItems = computed(() => {
    let shownItems = [];
    const hiddenItemNames = Array.isArray(props.hiddenItems?.items)
        ? props.hiddenItems.items
        : [];
    const hiddenHeaderNames = Array.isArray(props.hiddenItems?.headers)
        ? props.hiddenItems.headers
        : [];

    props.items.forEach((item) => {
        if (item.type !== 'heading' && !hiddenItemNames.includes(item.name)) {
            shownItems.push(item);
        }
        if (item.type === 'heading' && !hiddenHeaderNames.includes(item.name)) {
            shownItems.push(item);
        }
    });

    return shownItems;
});

const hierarchialNavigation = computed(() => {
    const hierarchy = [];
    let currentSection = null;

    filteredItems.value.forEach((item) => {
        if (item.type === 'heading') {
            currentSection = {
                ...item,
                subitems: [],
            };
            hierarchy.push(currentSection);
        } else if (item.standalone) {
            currentSection = null;
            hierarchy.push(item);
        } else if (currentSection) {
            currentSection.subitems.push(item);
        } else {
            // Top level items that don't belong to any section
            hierarchy.push(item);
        }
    });

    return hierarchy;
});

function itemIsActive(item) {
    if (item.routeName && route().current(item.routeName, item.routeParams)) {
        return true;
    }

    if (item.activeRoutes) {
        return item.activeRoutes.some((r) =>
            route().current(r, item.routeParams)
        );
    }

    return false;
}

function sectionHasActiveChild(section) {
    if (itemIsActive(section)) return true;
    if (!section.subitems) return false;
    return section.subitems.some((subitem) => {
        return itemIsActive(subitem);
    });
}

function onBeforeEnter(el) {
    el.style.maxHeight = '0';
    el.style.opacity = '0';
    el.style.overflow = 'hidden';
}

function onEnter(el, done) {
    el.style.transition = 'max-height 250ms ease-out, opacity 200ms ease-out';
    nextTick(() => {
        el.style.maxHeight = el.scrollHeight + 'px';
        el.style.opacity = '1';
    });
    el.addEventListener(
        'transitionend',
        function handler() {
            el.removeEventListener('transitionend', handler);
            el.style.maxHeight = '';
            el.style.overflow = '';
            done();
        },
        { once: true }
    );
}

function onLeave(el, done) {
    el.style.maxHeight = el.scrollHeight + 'px';
    el.style.overflow = 'hidden';
    el.offsetHeight; // force reflow
    el.style.transition = 'max-height 200ms ease-in, opacity 150ms ease-in';
    el.style.maxHeight = '0';
    el.style.opacity = '0';
    el.addEventListener(
        'transitionend',
        function handler() {
            el.removeEventListener('transitionend', handler);
            done();
        },
        { once: true }
    );
}

// Floating "island" shown while the sidebar is collapsed: a section's children,
// or just a compact label for items without children
const islandItem = ref(null);
const islandRef = ref(null);
const islandPosition = reactive({ top: 0, left: 0, anchorHeight: 0 });
const islandIsLabelOnly = computed(
    () => islandItem.value !== null && !hasSubitems(islandItem.value)
);
let islandOpenTimeout = null;
let islandCloseTimeout = null;

function hasSubitems(item) {
    return Array.isArray(item.subitems) && item.subitems.length > 0;
}

function clearIslandTimers() {
    clearTimeout(islandOpenTimeout);
    clearTimeout(islandCloseTimeout);
    islandOpenTimeout = null;
    islandCloseTimeout = null;
}

async function openIsland(item, anchorEl) {
    clearIslandTimers();

    const rect = anchorEl.getBoundingClientRect();
    islandItem.value = item;
    islandPosition.top = rect.top;
    islandPosition.left = rect.right;
    islandPosition.anchorHeight = rect.height;

    if (!hasSubitems(item)) return;

    // Keep the island inside the viewport for sections near the bottom
    await nextTick();
    if (!islandRef.value) return;
    const maxTop =
        window.innerHeight -
        islandRef.value.offsetHeight -
        ISLAND_VIEWPORT_MARGIN;
    islandPosition.top = Math.max(
        ISLAND_VIEWPORT_MARGIN,
        Math.min(rect.top, maxTop)
    );
}

function closeIsland() {
    clearIslandTimers();
    islandItem.value = null;
}

function scheduleIslandClose() {
    clearTimeout(islandOpenTimeout);
    islandOpenTimeout = null;
    if (!islandItem.value) return;

    // Labels aren't interactive, so there's nothing to move the cursor to
    if (islandIsLabelOnly.value) {
        closeIsland();
        return;
    }

    clearTimeout(islandCloseTimeout);
    islandCloseTimeout = setTimeout(closeIsland, ISLAND_CLOSE_DELAY);
}

function keepIslandOpen() {
    clearIslandTimers();
}

function handleItemMouseEnter(item, event) {
    if (props.showLabels) return;

    // A short delay lets the cursor cross other headings on its way to the island
    const anchorEl = event.currentTarget;
    clearTimeout(islandOpenTimeout);
    islandOpenTimeout = setTimeout(
        () => openIsland(item, anchorEl),
        ISLAND_OPEN_DELAY
    );
}

function handleSectionClick(item, event) {
    if (props.showLabels) {
        toggleSection(item);
        return;
    }

    // Clicking always opens (never toggles), since hover has usually opened it already.
    // This also covers touch devices, which have no hover.
    if (islandItem.value?.name !== item.name) {
        openIsland(item, event.currentTarget);
    } else {
        clearIslandTimers();
    }
}

function handleDocumentPointerDown(event) {
    if (!islandItem.value) return;
    if (islandRef.value?.contains(event.target)) return;
    if (event.target.closest?.('[data-sidebar-section]')) return;
    closeIsland();
}

function handleDocumentKeydown(event) {
    if (event.key === 'Escape') closeIsland();
}

watch(
    () => props.showLabels,
    (showLabels) => {
        if (showLabels) closeIsland();
    }
);

onBeforeUnmount(() => {
    clearIslandTimers();
    document.removeEventListener('pointerdown', handleDocumentPointerDown);
    document.removeEventListener('keydown', handleDocumentKeydown);
    window.removeEventListener('resize', closeIsland);
});

onMounted(() => {
    document.addEventListener('pointerdown', handleDocumentPointerDown);
    document.addEventListener('keydown', handleDocumentKeydown);
    window.addEventListener('resize', closeIsland);

    loadNotificationCounts();
    const storedSections = localStorage.getItem('expandedSections');
    if (storedSections) {
        const sections = JSON.parse(storedSections);
        sections.length > 0
            ? (expandedSections.value = [sections[sections.length - 1]])
            : [];
    }

    if (
        itemIsActive(dashboardNavigationItem) ||
        hierarchialNavigation.value.some(
            (item) => item.href && !item.subitems && itemIsActive(item)
        )
    ) {
        collapseExpandedSections();
    }
});
</script>
<template>
    <div class="flex flex-col h-full min-h-0 overflow-hidden">
        <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
            <!-- Top area (slot from backOffice.vue for slideouts etc.) -->
            <div class="flex-shrink-0">
                <slot name="top" />
            </div>

            <!-- Scrollable nav items -->
            <div
                class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-2 sidebar-scroll"
                id="navigation-main-scrollable"
                @scroll="closeIsland"
            >
                <ul class="">
                    <li
                        @mouseenter="handleItemMouseEnter(dashboardNavigationItem, $event)"
                        @mouseleave="scheduleIslandClose"
                    >
                        <a
                            :href="dashboardNavigationItem.href"
                            class="block"
                            @click="collapseExpandedSections"
                        >
                            <SidebarNavigationHeading
                                :item="dashboardNavigationItem"
                                :is-first="true"
                                :is-last="false"
                                :is-expandable="false"
                                :notification-count="false"
                                :show-label="showLabels"
                            />
                        </a>
                    </li>
                </ul>
                <ul class="mt-1">
                    <li
                        v-for="(item, i) in hierarchialNavigation"
                        :key="i"
                        @mouseenter="handleItemMouseEnter(item, $event)"
                        @mouseleave="scheduleIslandClose"
                    >
                        <a
                            v-if="item.href && !item.subitems"
                            :href="item.href"
                            class="block"
                            @click="collapseExpandedSections"
                        >
                            <SidebarNavigationHeading
                                :item="item"
                                :is-last="i === hierarchialNavigation.length - 1"
                                :is-expanded="false"
                                :is-expandable="false"
                                :has-active-child="sectionHasActiveChild(item)"
                                :show-label="showLabels"
                            />
                        </a>
                        <SidebarNavigationHeading
                            v-else
                            :item="item"
                            :is-last="i === hierarchialNavigation.length - 1"
                            :is-expanded="expandedSections.includes(item.name)"
                            :has-active-child="sectionHasActiveChild(item)"
                            :show-label="showLabels"
                            :class="[
                                'cursor-pointer',
                                !showLabels && islandItem?.name === item.name
                                    ? 'sidebar-section-island-open'
                                    : '',
                            ]"
                            data-sidebar-section
                            @click="handleSectionClick(item, $event)"
                        />
                        <Transition
                            @before-enter="onBeforeEnter"
                            @enter="onEnter"
                            @leave="onLeave"
                            :css="false"
                        >
                            <ul
                                v-if="
                                    expandedSections.includes(item.name) &&
                                    item.subitems &&
                                    item.subitems.length > 0 &&
                                    showLabels
                                "
                                class="mb-2 pl-5"
                            >
                                <li
                                    v-for="(subitem, si) in item.subitems"
                                    :key="si"
                                >
                                    <SidebarNavigationItem
                                        :item="subitem"
                                        :is-first="si === 0"
                                        :is-last="
                                            si === item.subitems.length - 1
                                        "
                                        :notification-count="
                                            notifications[subitem.itemName]
                                        "
                                        :show-label="showLabels"
                                    />
                                </li>
                            </ul>
                        </Transition>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Collapsed-state island with the hovered section's children -->
        <Teleport to="body">
            <Transition name="sidebar-island">
                <div
                    v-if="islandItem && islandIsLabelOnly"
                    ref="islandRef"
                    class="fixed z-sidebar flex items-center pl-2 pointer-events-none"
                    :style="{
                        top: `${islandPosition.top}px`,
                        left: `${islandPosition.left}px`,
                        height: `${islandPosition.anchorHeight}px`,
                    }"
                >
                    <div
                        class="px-3 py-1.5 text-sm font-medium tracking-tight text-gray-900 whitespace-nowrap bg-white border border-gray-200 rounded-lg shadow-lg"
                    >
                        {{ _mft(islandItem.name) }}
                    </div>
                </div>
                <div
                    v-else-if="islandItem"
                    ref="islandRef"
                    class="fixed z-sidebar pl-2"
                    :style="{
                        top: `${islandPosition.top}px`,
                        left: `${islandPosition.left}px`,
                    }"
                    @mouseenter="keepIslandOpen"
                    @mouseleave="scheduleIslandClose"
                >
                    <div
                        class="w-60 max-h-[calc(100vh-1rem)] overflow-y-auto sidebar-scroll pb-2 bg-white border border-gray-200 rounded-lg shadow-lg"
                    >
                        <div
                            class="px-4 py-2 mb-1 text-sm font-bold tracking-tight text-gray-900 border-b border-gray-100 bg-gray-50"
                        >
                            {{ _mft(islandItem.name) }}
                        </div>
                        <ul class="-ml-3">
                            <SidebarNavigationItem
                                v-for="(subitem, si) in islandItem.subitems"
                                :key="si"
                                :item="subitem"
                                :is-first="si === 0"
                                :is-last="si === islandItem.subitems.length - 1"
                                :notification-count="
                                    notifications[subitem.itemName]
                                "
                                :show-label="true"
                            />
                        </ul>
                    </div>
                </div>
            </Transition>
        </Teleport>

        <!-- Bottom area: dock + fixed actions -->
        <div
            class="flex-shrink-0 border-t border-gray-100"
            id="navigation-bottom-area"
        >
            <SidebarDock />
            <SidebarFixedActions
                :notification-count="notificationCount"
                :user-profile-image-url="userProfileImageUrl"
                @open-notifications="emit('open-notifications')"
            />
        </div>
    </div>
</template>
<style scoped>
.sidebar-island-enter-active,
.sidebar-island-leave-active {
    transition: opacity 120ms ease, transform 120ms ease;
}
.sidebar-island-enter-from,
.sidebar-island-leave-to {
    opacity: 0;
    transform: translateX(-4px);
}
.sidebar-section-island-open :deep(.rounded-md) {
    background-color: rgb(249 250 251);
    border-color: rgb(243 244 246);
}

.sidebar-scroll::-webkit-scrollbar {
    width: 4px;
}
.sidebar-scroll::-webkit-scrollbar-track {
    background: transparent;
}
.sidebar-scroll::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
}
.sidebar-scroll:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
}
</style>
